/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { DateInput, Form, Grid, Tooltip } from '@mairistem/composants';
import dayjs from 'dayjs';

import { Section } from '@/components/Common';
import {
  OPTIONS_PERIODE_RECENSEMENT,
  OPTIONS_RECENSE,
  OPTIONS_EXEMPTION,
} from '@/constants';
import { Exemption } from '@/types';

interface Props {
  anneeRecensement: number;
  dateRecensement: string;
  demandeExemption: string;
  recensement: string;
  trimestreRecensement: number;
  exemptionActive: boolean;
  errors: Record<string, string>;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement> | null,
    object: object,
  ) => void;
}

const Recensement = (props: Props) => {
  const {
    anneeRecensement,
    dateRecensement,
    demandeExemption,
    recensement,
    trimestreRecensement,
    exemptionActive,
    errors,
    onChange,
  } = props;

  const [dateRecensementObj, setDateRecensementObj] =
    React.useState<Date | null>(
      dateRecensement ? new Date(dateRecensement) : null,
    );

  React.useEffect(() => {
    setDateRecensementObj(dateRecensement ? new Date(dateRecensement) : null);
  }, [dateRecensement]);

  const handleDateChange = React.useCallback(
    (value: Date | null) => {
      setDateRecensementObj(value);

      onChange(null, {
        name: 'dateRecensement',
        value: value ? dayjs(value).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  const handleCheckboxChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    { checked },
  ) => {
    if (!checked) {
      onChange(null, {
        name: 'demandeExemption',
        value: Exemption.SansObjet,
      });
    }
    onChange(null, {
      name: 'exemptionActive',
      value: checked,
    });
  };

  const handleYearInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    { name, value },
  ) => {
    let number = value.replace(/[^0-9]/g, ''); // Garde uniquement les chiffres

    if (number.length > 4) {
      number = number.slice(0, 4); // Limite à 4 chiffres
    }

    const maxYear = dayjs().year() + 1; // Année actuelle + 1
    let newValue = number;

    if (number.length === 4 && parseInt(number, 10) > maxYear) {
      newValue = number.slice(0, 3); // Bloque les années supérieures à n+1
    }

    onChange(e, { name, value: newValue });
  };

  return (
    <Section title="RECENSEMENT">
      <Grid padded as={Form}>
        <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
          <Grid.Column width={16}>
            <Form.Group>
              {/** display block car la dropdown ajoute un espace */}
              <Tooltip
                trigger={
                  <Form.Dropdown
                    required
                    selection
                    name="trimestreRecensement"
                    label="Recensé sur le"
                    data-testid="recensement-dropdown-trimestre"
                    options={OPTIONS_PERIODE_RECENSEMENT}
                    value={trimestreRecensement}
                    onChange={onChange}
                    style={{ width: 150, display: 'block' }}
                  />
                }
                type={errors?.trimestreRecensement ? 'error' : ''}
                message={errors?.trimestreRecensement}
              />
              <Tooltip
                trigger={
                  <Form.Input
                    required
                    name="anneeRecensement"
                    label="de"
                    type="number"
                    data-testid="recensement-input-annee"
                    value={anneeRecensement}
                    onChange={handleYearInput}
                    style={{ width: 90 }}
                  />
                }
                type={errors?.anneeRecensement ? 'error' : ''}
                message={errors?.anneeRecensement}
              />
              <Form.Input required label="en date du">
                <Tooltip
                  trigger={
                    <DateInput
                      value={dateRecensementObj}
                      onChange={handleDateChange}
                      data-testid="recensement-input-date"
                      // @ts-expect-error style
                      style={{ height: 38 }}
                    />
                  }
                  type={errors?.dateRecensement ? 'error' : ''}
                />
              </Form.Input>
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
          <Grid.Column width={16}>
            <Form.Group inline>
              <Form.Field required label="Inscrit sur la liste" />
              {_.map(OPTIONS_RECENSE, (option) => (
                <Form.Field>
                  <Form.Radio
                    name="recensement"
                    label={option.text}
                    value={option.value}
                    checked={option.value === recensement}
                    data-testid={`recensement-radio-${option.text}`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(event, {
                        name: 'recensement',
                        value: option.value,
                      });
                    }}
                  />
                </Form.Field>
              ))}
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row verticalAlign="middle" style={{ padding: '1em' }}>
          <Grid.Column mobile={16} largeScreen={5} widescreen={5}>
            <Form.Checkbox
              label="Demande d'exemption"
              data-testid="recensement-checkbox-exemption"
              checked={exemptionActive}
              onChange={handleCheckboxChange}
            />
          </Grid.Column>
          <Grid.Column
            mobile={16}
            largeScreen={11}
            widescreen={11}
            style={{ paddingLeft: 0, paddingRight: 0 }}
          >
            {_.map(OPTIONS_EXEMPTION, (option) => (
              <Form.Field>
                <Tooltip
                  trigger={
                    <Form.Checkbox
                      radio
                      name="demandeExemption"
                      label={option.text}
                      value={option.value}
                      checked={demandeExemption === option.value}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        onChange(event, {
                          name: 'demandeExemption',
                          value: option.value,
                        });
                      }}
                      disabled={!exemptionActive}
                    />
                  }
                  type={errors?.demandeExemption ? 'error' : ''}
                  message={errors?.demandeExemption}
                />
              </Form.Field>
            ))}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Section>
  );
};

export default Recensement;
